import { useHotelPricingConfig } from '@/shared/hooks/use-hotel-pricing-config'
import { BufferedNumberInput } from '@/shared/ui/buffered-number-input'
import { Label } from '@/shared/ui/label'
import { Textarea } from '@/shared/ui/textarea'

const sectionTitleClass = 'text-sm font-semibold'
const subsectionTitleClass = 'text-sm font-medium text-muted-foreground'

function PriceInput({
    label,
    value,
    onChange,
    suffix = '€',
}: {
    label: string
    value: number | undefined
    onChange: (value: number) => void
    suffix?: string
}) {
    return (
        <div className='space-y-2'>
            <Label>{label}</Label>
            <div className='flex items-center gap-2'>
                <BufferedNumberInput
                    initialValue={value ?? ''}
                    onDebouncedChange={onChange}
                    className='w-24'
                />
                <span className='text-sm text-muted-foreground'>{suffix}</span>
            </div>
        </div>
    )
}

export function HotelPricingForm() {
    const { config, updateConfig } = useHotelPricingConfig()

    return (
        <div className='space-y-8'>
            <section className='space-y-4'>
                <h3 className={sectionTitleClass}>Tarifas base</h3>
                <div className='grid gap-6 md:grid-cols-2'>
                    <PriceInput
                        label='Perro pequeño (por día)'
                        value={config?.smallDogPrice}
                        onChange={smallDogPrice => updateConfig({ smallDogPrice })}
                    />
                    <PriceInput
                        label='Perro mediano (por día)'
                        value={config?.mediumDogPrice}
                        onChange={mediumDogPrice => updateConfig({ mediumDogPrice })}
                    />
                    <PriceInput
                        label='Perro grande (por día)'
                        value={config?.largeDogPrice}
                        onChange={largeDogPrice => updateConfig({ largeDogPrice })}
                    />
                    <PriceInput
                        label='Guardería (por día)'
                        value={config?.daycarePrice}
                        onChange={daycarePrice => updateConfig({ daycarePrice })}
                    />
                    <PriceInput
                        label='Incremento temporada alta'
                        value={config?.highSeasonIncrease}
                        onChange={highSeasonIncrease => updateConfig({ highSeasonIncrease })}
                        suffix='€/día'
                    />
                    <PriceInput
                        label='IVA'
                        value={config?.iva}
                        onChange={iva => updateConfig({ iva })}
                        suffix='%'
                    />
                </div>
            </section>

            <section className='space-y-6 border-t pt-8'>
                <h3 className={sectionTitleClass}>Tarifas por cliente</h3>

                <div className='grid gap-8 md:grid-cols-2'>
                    <div className='space-y-4'>
                        <h4 className={subsectionTitleClass}>Perros pequeños — cliente regular</h4>
                        <PriceInput
                            label='1 perro'
                            value={config?.oneSmallPetAndRegularClientPrice}
                            onChange={oneSmallPetAndRegularClientPrice =>
                                updateConfig({ oneSmallPetAndRegularClientPrice })
                            }
                        />
                        <PriceInput
                            label='2 perros'
                            value={config?.twoSmallPetsAndRegularClientPrice}
                            onChange={twoSmallPetsAndRegularClientPrice =>
                                updateConfig({ twoSmallPetsAndRegularClientPrice })
                            }
                        />
                        <PriceInput
                            label='3 perros'
                            value={config?.threeSmallPetsAndRegularClientPrice}
                            onChange={threeSmallPetsAndRegularClientPrice =>
                                updateConfig({ threeSmallPetsAndRegularClientPrice })
                            }
                        />
                    </div>

                    <div className='space-y-4'>
                        <h4 className={subsectionTitleClass}>Perros pequeños — cliente VIP</h4>
                        <PriceInput
                            label='1 perro'
                            value={config?.oneSmallPetAndVIPClientPrice}
                            onChange={oneSmallPetAndVIPClientPrice =>
                                updateConfig({ oneSmallPetAndVIPClientPrice })
                            }
                        />
                        <PriceInput
                            label='2 perros'
                            value={config?.twoSmallPetsAndVIPClientPrice}
                            onChange={twoSmallPetsAndVIPClientPrice => updateConfig({ twoSmallPetsAndVIPClientPrice })}
                        />
                        <PriceInput
                            label='3 perros'
                            value={config?.threeSmallPetsAndVIPClientPrice}
                            onChange={threeSmallPetsAndVIPClientPrice =>
                                updateConfig({ threeSmallPetsAndVIPClientPrice })
                            }
                        />
                    </div>

                    <div className='space-y-4'>
                        <h4 className={subsectionTitleClass}>Perros medianos — cliente regular</h4>
                        <PriceInput
                            label='1 perro'
                            value={config?.oneMediumPetAndRegularClientPrice}
                            onChange={oneMediumPetAndRegularClientPrice =>
                                updateConfig({ oneMediumPetAndRegularClientPrice })
                            }
                        />
                        <PriceInput
                            label='2 perros'
                            value={config?.twoMediumPetsAndRegularClientPrice}
                            onChange={twoMediumPetsAndRegularClientPrice =>
                                updateConfig({ twoMediumPetsAndRegularClientPrice })
                            }
                        />
                        <PriceInput
                            label='3 perros'
                            value={config?.threeMediumPetsAndRegularClientPrice}
                            onChange={threeMediumPetsAndRegularClientPrice =>
                                updateConfig({ threeMediumPetsAndRegularClientPrice })
                            }
                        />
                    </div>

                    <div className='space-y-4'>
                        <h4 className={subsectionTitleClass}>Perros medianos — cliente VIP</h4>
                        <PriceInput
                            label='1 perro'
                            value={config?.oneMediumPetAndVIPClientPrice}
                            onChange={oneMediumPetAndVIPClientPrice =>
                                updateConfig({ oneMediumPetAndVIPClientPrice })
                            }
                        />
                        <PriceInput
                            label='2 perros'
                            value={config?.twoMediumPetsAndVIPClientPrice}
                            onChange={twoMediumPetsAndVIPClientPrice =>
                                updateConfig({ twoMediumPetsAndVIPClientPrice })
                            }
                        />
                        <PriceInput
                            label='3 perros'
                            value={config?.threeMediumPetsAndVIPClientPrice}
                            onChange={threeMediumPetsAndVIPClientPrice =>
                                updateConfig({ threeMediumPetsAndVIPClientPrice })
                            }
                        />
                    </div>

                    <div className='space-y-4'>
                        <h4 className={subsectionTitleClass}>Perros grandes — cliente regular</h4>
                        <PriceInput
                            label='1 perro'
                            value={config?.oneBigPetAndRegularClientPrice}
                            onChange={oneBigPetAndRegularClientPrice =>
                                updateConfig({ oneBigPetAndRegularClientPrice })
                            }
                        />
                        <PriceInput
                            label='2 perros'
                            value={config?.twoBigPetsAndRegularClientPrice}
                            onChange={twoBigPetsAndRegularClientPrice =>
                                updateConfig({ twoBigPetsAndRegularClientPrice })
                            }
                        />
                        <PriceInput
                            label='3 perros'
                            value={config?.threeBigPetsAndRegularClientPrice}
                            onChange={threeBigPetsAndRegularClientPrice =>
                                updateConfig({ threeBigPetsAndRegularClientPrice })
                            }
                        />
                    </div>

                    <div className='space-y-4'>
                        <h4 className={subsectionTitleClass}>Perros grandes — cliente VIP</h4>
                        <PriceInput
                            label='1 perro'
                            value={config?.oneBigPetAndVIPClientPrice}
                            onChange={oneBigPetAndVIPClientPrice => updateConfig({ oneBigPetAndVIPClientPrice })}
                        />
                        <PriceInput
                            label='2 perros'
                            value={config?.twoBigPetsAndVIPClientPrice}
                            onChange={twoBigPetsAndVIPClientPrice => updateConfig({ twoBigPetsAndVIPClientPrice })}
                        />
                        <PriceInput
                            label='3 perros'
                            value={config?.threeBigPetsAndVIPClientPrice}
                            onChange={threeBigPetsAndVIPClientPrice =>
                                updateConfig({ threeBigPetsAndVIPClientPrice })
                            }
                        />
                    </div>

                    <div className='space-y-4 md:col-span-2 md:max-w-md'>
                        <h4 className={subsectionTitleClass}>Combinaciones</h4>
                        <PriceInput
                            label='1 pequeño + 1 mediano'
                            value={config?.oneSmallOneMediumPetPrice}
                            onChange={oneSmallOneMediumPetPrice => updateConfig({ oneSmallOneMediumPetPrice })}
                        />
                        <PriceInput
                            label='1 pequeño + 1 grande'
                            value={config?.oneSmallOneBigPetPrice}
                            onChange={oneSmallOneBigPetPrice => updateConfig({ oneSmallOneBigPetPrice })}
                        />
                        <PriceInput
                            label='1 mediano + 1 grande'
                            value={config?.oneMediumOneBigPetPrice}
                            onChange={oneMediumOneBigPetPrice => updateConfig({ oneMediumOneBigPetPrice })}
                        />
                    </div>
                </div>
            </section>

            <section className='space-y-6 border-t pt-8'>
                <h3 className={sectionTitleClass}>Servicios adicionales</h3>

                <div className='grid gap-6 md:grid-cols-2'>
                    <PriceInput
                        label='Comida especial (por día)'
                        value={config?.specialFood.price}
                        onChange={price =>
                            updateConfig({
                                specialFood: { ...config?.specialFood, price },
                            })
                        }
                    />
                    <PriceInput
                        label='Curas (por día)'
                        value={config?.healing.price}
                        onChange={price =>
                            updateConfig({
                                healing: { ...config?.healing, price },
                            })
                        }
                    />
                    <PriceInput
                        label='Medicación — una vez al día'
                        value={config?.medication.once.price}
                        onChange={price =>
                            updateConfig({
                                medication: {
                                    ...config?.medication,
                                    once: { ...config?.medication.once, price },
                                },
                            })
                        }
                    />
                    <PriceInput
                        label='Medicación — varias veces al día'
                        value={config?.medication.multiple.price}
                        onChange={price =>
                            updateConfig({
                                medication: {
                                    ...config?.medication,
                                    multiple: { ...config?.medication.multiple, price },
                                },
                            })
                        }
                    />
                </div>

                <div className='space-y-2'>
                    <Label>Ejemplos de precios por ubicación (transporte)</Label>
                    <Textarea
                        value={config?.driver.priceExamples || ''}
                        onChange={e =>
                            updateConfig({
                                driver: {
                                    ...config?.driver,
                                    priceExamples: e.target.value,
                                },
                            })
                        }
                        placeholder='Barcelona - Resort 42€&#10;Sant Cugat - Resort 42€&#10;Sant Just - Resort 52€&#10;Matadepera - Resort 63€&#10;Castelldefels - Resort 93€'
                        className='min-h-[120px]'
                    />
                    <p className='text-xs text-muted-foreground'>Introduce cada ejemplo en una línea separada.</p>
                </div>
            </section>
        </div>
    )
}
